import { FormControlLabel, Grid, Switch } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useAssetCategoriesByTransportModeIdQuery, useGetAssetClassesByCategoryQuery, useGetAssetTypesByCategoryQuery } from '../../../app/redux-fetch/apiAssets'
import { useGetTransportModeQuery } from '../../../app/redux-fetch/apiCoreQuery'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormDatePickerInput from '../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import { messages } from '../messages'

interface AssetAttributeGroupProps {
  disabled?: boolean
}

export function AssetSettingsGroup({ disabled }: AssetAttributeGroupProps) {
  const { formatMessage } = useIntl()
  const { control, watch, setValue, formState: { errors } } = useFormContext()
  const tenantId = watch('tenantId')
  const transportModeId = watch('transportModeId')
  const assetCategoryId = watch('assetCategoryId')
  const detailIsOwned = watch('detail.isOwned')
  const isUsedForExport = watch('detail.isUsedForExport')
  const exportDocumentationAvailable = watch('detail.exportDocumentationAvailable')

  const { data: transportModesData = [] } = useGetTransportModeQuery()
  const { data: assetCategoriesData = [], isLoading: isLoadingAssetCategories } = useAssetCategoriesByTransportModeIdQuery({ tenantId, transportModeId })
  const { data: assetTypesData = [], isLoading: isLoadingAssetTypes } = useGetAssetTypesByCategoryQuery({ tenantId, assetCategoryId })
  const { data: assetClassesData = [], isLoading: isLoadingAssetClasses } = useGetAssetClassesByCategoryQuery({ tenantId, assetCategoryId })

  const isDisabled = disabled || isLoadingAssetCategories || isLoadingAssetTypes || isLoadingAssetClasses

  return (
    <DefaultSpacingGrid container sx={{ pl: '16px', pt: '0px' }}>
      <Grid item xs={12} md={12}>
        <FormSelect
          name='transportModeId'
          label={formatMessage(messages.transportMode)}
          options={transportModesData.map((tm: any) => ({ label: tm.name, value: tm.id }))}
          disabled={isDisabled}
          required
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <FormSelect
          name='assetCategoryId'
          label='Asset Category'
          options={Array.isArray(assetCategoriesData) ? assetCategoriesData.map((ac: any) => ({ label: ac.name, value: ac.id })) : []}
          disabled={isDisabled || !Array.isArray(assetCategoriesData) || assetCategoriesData.length === 0}
          required
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <FormSelect
          name='assetTypeId'
          label={formatMessage(messages.assetsTypes)}
          options={Array.isArray(assetTypesData) ? assetTypesData.map((at: any) => ({ label: at.name, value: at.id })) : []}
          disabled={isDisabled || !Array.isArray(assetTypesData) || assetTypesData.length === 0}
          required
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <FormSelect
          name='assetClassId'
          label={formatMessage(messages.assetsClasses)}
          options={Array.isArray(assetClassesData) ? assetClassesData.map((ac: any) => ({ label: ac.name, value: ac.id })) : []}
          disabled={isDisabled || !Array.isArray(assetClassesData) || assetClassesData.length === 0}
          required
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <FormInput
          name='identification'
          label={formatMessage(messages.assetsDetailsIdentification)}
          disabled={isDisabled}
          maxLength={50}
          required
        />
      </Grid>
      {detailIsOwned === false && (
        <Grid item xs={12} md={12}>
          <FormInput
            name='detail.ownerName'
            label={formatMessage(messages.ownershipName)}
            disabled={isDisabled}
            maxLength={100}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Controller
          name='detail.isUsedForExport'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch color='secondary' {...field} checked={!!field.value} disabled={isDisabled} />}
              label={formatMessage(messages.isUsedForExport)}
              labelPlacement='start'
              sx={{ minHeight: '56px', ml: 0 }}
            />
          )}
        />
      </Grid>
      {isUsedForExport && (
        <>
          <Grid item xs={12}>
            <Controller
              name='detail.exportDocumentationAvailable'
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch color='secondary' {...field} checked={!!field.value} disabled={isDisabled} />}
                  label={formatMessage(messages.exportDocumentationAvailable)}
                  labelPlacement='start'
                  sx={{ minHeight: '56px', ml: 0 }}
                />
              )}
            />
          </Grid>
          {exportDocumentationAvailable && (
            <Grid item xs={12} md={12}>
              <FormDatePickerInput
                name='detail.exportDocumentationExpiry'
                label={formatMessage(messages.exportDocumentationExpiry)}
                disabled={isDisabled}
                minDate={new Date()}
              />
            </Grid>
          )}
        </>
      )}
      <Grid item xs={12} md={12}>
        <Controller
          name='detail.isOwned'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ justifyContent: 'start', minHeight: '56px', ml: '0px' }}
              checked={!!field.value}
              onChange={(_, checked) => {
                field.onChange(checked)
                if (!checked) setValue('detail.ownerName', '')
              }}
              control={<Switch color='secondary' />}
              label={formatMessage(messages.assetsDetailsIsOwned)}
              labelPlacement='start'
              disabled={isDisabled}
            />
          )}
        />
      </Grid>

    </DefaultSpacingGrid>
  )
}
